/**
 *  알림톡 발송 내역 도메인 타입
 *
 *  백엔드 SSOT: docs/product-specs/admin/alimtalk-send-history-SPEC.md (PR #4982, AMZBM-43)
 *  - GET  /api/alimtalk-send-histories      : 목록 조회
 *  - GET  /api/alimtalk-send-histories/:id  : 상세 조회
 *  - DELETE /api/alimtalk-sends/:id         : 예약 취소 (기존 API 재사용)
 *
 *  프론트 openapi 자동 생성 타입이 아직 없어(백엔드 미배포) 스펙 기준으로 직접 선언한다.
 */

export const ALIMTALK_SEND_STATUS = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  CANCELLING: 'CANCELLING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  TEST_COMPLETED: 'TEST_COMPLETED',
} as const;

export type AlimtalkSendStatus = (typeof ALIMTALK_SEND_STATUS)[keyof typeof ALIMTALK_SEND_STATUS];

export const ALIMTALK_SEND_TYPE = {
  CSV: 'CSV',
  ISSUE_COUPON_ONLY: 'ISSUE_COUPON_ONLY',
  ISSUE_COUPON: 'ISSUE_COUPON',
  EXPIRE_COUPON: 'EXPIRE_COUPON',
} as const;

export type AlimtalkSendType = (typeof ALIMTALK_SEND_TYPE)[keyof typeof ALIMTALK_SEND_TYPE];

export type AlimtalkSendHistorySearchType = 'scheduleId' | 'memo' | 'adminName';

export interface AlimtalkSendHistoryListQueryParams {
  /** 예약일시 시작 (ISO 8601 + timezone) */
  startDate?: string;
  /** 예약일시 종료 (ISO 8601 + timezone) */
  endDate?: string;
  status?: AlimtalkSendStatus;
  sendType?: AlimtalkSendType;
  searchType?: AlimtalkSendHistorySearchType;
  searchValue?: string;
  /** 기본 1, 최대 100,000 */
  page?: number;
  /** 기본 20, 최대 100 */
  pageSize?: number;
}

export interface AlimtalkSendHistoryAdmin {
  id: number;
  name: string;
}

export interface AlimtalkSendHistoryItem {
  id: number;
  scheduledAt: string;
  sendType: AlimtalkSendType | null;
  status: AlimtalkSendStatus;
  /** AlimtalkSendSchedule.totalCount (중복 제거된 대상 수) */
  targetCount: number | null;
  /** 최종 S3 summary.processed */
  processedCount: number | null;
  /** 최종 S3 summary.success */
  successCount: number | null;
  /** 최종 S3 summary.failed */
  failedCount: number | null;
  memo: string;
  admin: AlimtalkSendHistoryAdmin;
  isTest: boolean;
  /** PENDING / RUNNING 일 때만 true */
  canCancel: boolean;
}

export interface AlimtalkSendHistoryListResponse {
  /** 검색 조건에 맞는 전체 예약 건수 (DB 기준) */
  totalCount: number;
  page: number;
  pageSize: number;
  sends: AlimtalkSendHistoryItem[];
}

export interface AlimtalkSendFailureReason {
  code: string;
  description: string;
  count: number;
}

export interface AlimtalkSendResult {
  processedCount: number | null;
  successCount: number | null;
  failedCount: number | null;
  failureReasons: AlimtalkSendFailureReason[];
}

export interface AlimtalkSendHistoryCoupon {
  id: number;
  name: string | null;
}

export interface AlimtalkSendHistoryDetail {
  id: number;
  scheduledAt: string;
  sendType: AlimtalkSendType | null;
  status: AlimtalkSendStatus;
  memo: string;
  admin: AlimtalkSendHistoryAdmin;
  isTest: boolean;
  testPhoneMasked: string | null;
  /** 작업 자체가 실패한 경우의 Worker 처리 오류 */
  errorMessage: string | null;
  targetCount: number | null;
  result: AlimtalkSendResult;
  coupons: AlimtalkSendHistoryCoupon[];
  canCancel: boolean;
}

export interface AlimtalkSendHistoryDetailResponse {
  send: AlimtalkSendHistoryDetail;
}

/**
 *  DELETE /api/alimtalk-sends/:id 응답 (AlimtalkSendResponseDto).
 *  화면에서는 id / status 만 사용하므로 나머지는 선택 필드로 둔다.
 */
export interface AlimtalkSendCancelResponse {
  id: number;
  status: AlimtalkSendStatus;
}
